/**
 * Kanban Column Badge Component
 * @implements FS-005.6.2 - View Kanban Board (column display)
 * @implements FS-005.2.1 - Kanban Column Structure
 * @implements US-037 - Basic Roadmap Viewing
 *
 * Displays a badge indicating the kanban column of a roadmap item.
 * Uses distinct colors for each of the 7 columns.
 * Consolidation displays as "Validating" and QA displays as "Testing".
 */

import { Chip, ChipProps } from '@mui/material';

/**
 * Kanban column values matching the backend enum
 * @implements FS-005.2.1 - Kanban Column Structure
 */
export type KanbanColumn =
  | 'backlog'
  | 'consolidation'
  | 'todo'
  | 'inProgress'
  | 'review'
  | 'qa'
  | 'done';

/**
 * Configuration for each kanban column badge
 * @implements US-037 - Visual States
 */
const COLUMN_CONFIG: Record<
  KanbanColumn,
  { label: string; color: ChipProps['color']; variant: ChipProps['variant'] }
> = {
  backlog: { label: 'Backlog', color: 'default', variant: 'outlined' },
  consolidation: {
    label: 'Validating',
    color: 'warning',
    variant: 'filled',
  },
  todo: { label: 'To Do', color: 'info', variant: 'outlined' },
  inProgress: { label: 'In Progress', color: 'info', variant: 'filled' },
  review: { label: 'Review', color: 'secondary', variant: 'filled' },
  qa: { label: 'Testing', color: 'secondary', variant: 'outlined' },
  done: { label: 'Done', color: 'success', variant: 'filled' },
};

/**
 * Props for KanbanColumnBadge component
 */
export interface KanbanColumnBadgeProps {
  /** The kanban column to display */
  column: KanbanColumn;
  /** Size of the badge */
  size?: 'small' | 'medium';
  /** Whether this is a derived column (for milestones) */
  isDerived?: boolean;
}

/**
 * KanbanColumnBadge - Displays the kanban column as a colored chip
 *
 * @implements FS-005.6.2 - View Kanban Board (column display)
 * @implements US-037 - Visual States
 *
 * Features:
 * - Distinct colors for all 7 columns
 * - Consolidation shows as "Validating"
 * - QA shows as "Testing"
 * - Optional "Derived" suffix for milestone columns
 */
export const KanbanColumnBadge = ({
  column,
  size = 'small',
  isDerived = false,
}: KanbanColumnBadgeProps) => {
  const config = COLUMN_CONFIG[column] ?? {
    label: column,
    color: 'default' as const,
    variant: 'outlined' as const,
  };

  const label = isDerived ? `${config.label} (derived)` : config.label;

  return (
    <Chip
      label={label}
      size={size}
      color={config.color}
      variant={config.variant}
      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
    />
  );
};
